/** Setting key for shared-cost description keywords (flat phrase list). */
export const SHARED_COST_KEYWORDS_KEY = 'shared_cost_keywords'

export type SharedCostKind =
  | 'tax'
  | 'shipping'
  | 'discount'
  | 'personal_shopper'
  | 'express'
  | 'tip'
  | 'fee'

/** Built-in phrases grouped by kind — used only to build the flat default inventory. */
const DEFAULT_SHARED_COST_KEYWORDS_BY_KIND: Record<SharedCostKind, readonly string[]> = {
  tax: [
    'tax',
    'sales tax',
    'sale tax',
    'state tax',
    'local tax',
    'use tax',
    'transaction tax',
    'vat',
    'gst',
    'hst',
  ],
  shipping: [
    'shipping',
    'shipping and handling',
    'freight',
    'delivery',
    'handling',
    'tariff',
    's&h',
    's & h',
  ],
  discount: ['discount', 'credit applied', 'promo'],
  personal_shopper: [
    'personal shopper',
    'shopper fee',
    'your shopper',
    'priority picker',
    'picker fee',
  ],
  express: ['express'],
  tip: ['tip', 'tips', 'gratuity', 'driver tip', 'cart tip', 'optional tip'],
  fee: [
    'inventory maintenance charge',
    'service charge',
    'setup fee',
    'setup fees',
    'service fee',
    'service fees',
    'processing fee',
    'processing fees',
    'handling fee',
    'handling fees',
    'bag fee',
    'bag fees',
    'bottle deposit',
    'bottle fee',
    'bottle fees',
    'delivery fee',
    'delivery fees',
    'convenience fee',
    'convenience fees',
    'fuel surcharge',
    'energy surcharge',
    'membership fee',
    'membership fees',
    'small order fee',
    'basket fee',
    'below order min',
    'below order minimum',
  ],
}

const OLD_V1_TRIGGERS = new Set(['inventory maintenance charge', 'service charge'])

function normalizeKeywordList(raw: unknown): string[] {
  if (!Array.isArray(raw)) return []
  return raw
    .filter((x): x is string => typeof x === 'string')
    .map((x) => x.trim().toLowerCase())
    .filter(Boolean)
}

function normalizeAndSort(keywords: Iterable<string>): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const kw of [...keywords].map((k) => k.trim().toLowerCase()).filter(Boolean).sort()) {
    if (!seen.has(kw)) {
      seen.add(kw)
      out.push(kw)
    }
  }
  return out
}

/** Full built-in phrase inventory, alphabetically sorted. */
export const DEFAULT_SHARED_COST_KEYWORDS: readonly string[] = normalizeAndSort(
  Object.values(DEFAULT_SHARED_COST_KEYWORDS_BY_KIND).flat(),
)

const EXPANDED_BUILTIN_KEYWORDS = new Set(
  DEFAULT_SHARED_COST_KEYWORDS.filter((kw) => !OLD_V1_TRIGGERS.has(kw)),
)

const EXCLUDED_SHARED_COST_DESCRIPTIONS = new Set(['automated sales tax management'])

function normalizedDescription(description: string | null | undefined): string | null {
  if (!description) return null
  const text = description.trim().toLowerCase()
  return text || null
}

export function isExcludedSharedCostDescription(
  description: string | null | undefined,
): boolean {
  const text = normalizedDescription(description)
  return text !== null && EXCLUDED_SHARED_COST_DESCRIPTIONS.has(text)
}

function looksLikeLegacyV1FlatArray(keywords: string[]): boolean {
  return !keywords.some((kw) => EXPANDED_BUILTIN_KEYWORDS.has(kw))
}

function flattenLegacyObject(data: Record<string, unknown>): string[] {
  const out: string[] = []
  for (const kind of Object.keys(DEFAULT_SHARED_COST_KEYWORDS_BY_KIND) as SharedCostKind[]) {
    if (kind in data) {
      out.push(...normalizeKeywordList(data[kind]))
    } else {
      out.push(...DEFAULT_SHARED_COST_KEYWORDS_BY_KIND[kind])
    }
  }
  return normalizeAndSort(out)
}

/** Parse stored JSON; legacy object/short array migrate to flat list. */
export function parseSharedCostKeywords(raw: string | null | undefined): string[] {
  const defaults = [...DEFAULT_SHARED_COST_KEYWORDS]
  if (!raw?.trim()) return defaults
  try {
    const parsed = JSON.parse(raw) as unknown
    if (Array.isArray(parsed)) {
      const keywords = normalizeKeywordList(parsed)
      if (keywords.length === 0) return defaults
      if (looksLikeLegacyV1FlatArray(keywords)) {
        return normalizeAndSort(new Set([...defaults, ...keywords]))
      }
      return normalizeAndSort(keywords)
    }
    if (parsed && typeof parsed === 'object') {
      return flattenLegacyObject(parsed as Record<string, unknown>)
    }
  } catch {
    /* ignore invalid JSON */
  }
  return defaults
}

/** Keywords shown in the settings editor (empty/missing setting → defaults). */
export function sharedCostKeywordsForEditor(raw: string | null | undefined): string[] {
  return parseSharedCostKeywords(raw)
}

const TIP_KEYWORDS = new Set(['tip', 'tips', 'gratuity', 'driver tip', 'cart tip', 'optional tip'])

const GRATUITY_LINE_PATTERNS = [
  /^(?:optional\s+)?tips?[\s.,:;!?]*$/i,
  /^gratuity[\s.,:;!?]*$/i,
  /^(?:driver|cart)\s+tips?[\s.,:;!?]*$/i,
]

/** True for standalone tip/gratuity charge lines, not product names containing "tip". */
export function isGratuityLineDescription(description: string | null | undefined): boolean {
  if (!description) return false
  const text = description.trim().toLowerCase()
  if (!text) return false
  const stripped = text.replace(/[\s.,:;!?]+$/, '')
  return GRATUITY_LINE_PATTERNS.some((rx) => rx.test(stripped))
}

function descriptionContainsTipWord(description: string | null | undefined): boolean {
  if (!description) return false
  const text = description.trim().toLowerCase()
  return /\btips?\b/i.test(text)
}

/** Honor is_tip unless the description is a product name containing "tip". */
export function isTipFlagTrustworthy(
  isTip: boolean | undefined,
  description: string | null | undefined,
): boolean {
  if (!isTip) return false
  if (isGratuityLineDescription(description)) return true
  if (!description?.trim()) return true
  if (descriptionContainsTipWord(description)) return false
  return true
}

function keywordMatches(textLower: string, keyword: string): boolean {
  const kw = keyword.trim().toLowerCase()
  if (!kw) return false
  if (TIP_KEYWORDS.has(kw)) return isGratuityLineDescription(textLower)
  if (kw.includes(' ')) return textLower.includes(kw)
  const escaped = kw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  return new RegExp(`\\b${escaped}\\b`, 'i').test(textLower)
}

export function descriptionMatchesSharedCostKeyword(
  description: string | null | undefined,
  keywords: readonly string[],
): boolean {
  if (isExcludedSharedCostDescription(description)) return false
  if (!description) return false
  const text = description.trim().toLowerCase()
  if (!text || text.startsWith('allocated ')) return false
  return keywords.some((keyword) => keywordMatches(text, keyword))
}

function classifyKindFromRegex(textLower: string): SharedCostKind | null {
  const discount = [/\bdiscount\b/i, /credit\s*applied/i, /\bpromo\b/i]
  for (const rx of discount) {
    if (rx.test(textLower)) return 'discount'
  }
  const personalShopper = [
    /personal\s*shopper/i,
    /shopper\s*fee/i,
    /your\s*shopper/i,
    /priority\s*picker/i,
    /picker\s*fee/i,
  ]
  for (const rx of personalShopper) {
    if (rx.test(textLower)) return 'personal_shopper'
  }
  if (/\bExpress\b/i.test(textLower)) return 'express'
  if (isGratuityLineDescription(textLower)) return 'tip'
  const fee = [
    /\bsetup\s+fees?\b/i,
    /\bservice\s+fees?\b/i,
    /\bprocessing\s+fees?\b/i,
    /\bhandling\s+fees?\b/i,
    /\bbag\s+fees?\b/i,
    /\bbottle\s+(?:deposit|fees?)\b/i,
    /\bdelivery\s+fees?\b/i,
    /\bconvenience\s+fees?\b/i,
    /\b(?:fuel|energy)\s+surcharge\b/i,
    /\bmembership\s+fees?\b/i,
    /\b(?:small\s*order|basket)\s+fees?\b/i,
    /\bbelow\s+order\s+min(?:imum)?\b/i,
    /(?:^|\s)fees?\s*(?:\(|:)/i,
    /(?:^|\s)fee\s+(?:\(|:)/i,
  ]
  for (const rx of fee) {
    if (rx.test(textLower)) return 'fee'
  }
  const shipping = [
    /\bshipping\b/i,
    /\bshipping\s+and\s+handling\b/i,
    /\bfreight\b/i,
    /\bdelivery\b/i,
    /\bhandling\b/i,
    /\btariff\b/i,
    /\bs\s*&\s*h\b/i,
  ]
  for (const rx of shipping) {
    if (rx.test(textLower)) return 'shipping'
  }
  const tax = [
    /(?:^|\s)(?:sales?\s*)?tax(?:\s|$)/i,
    /(?:^|\s)state\s*tax(?:\s|$)/i,
    /(?:^|\s)local\s*tax(?:\s|$)/i,
    /(?:^|\s)use\s*tax(?:\s|$)/i,
    /(?:^|\s)transaction\s*tax(?:\s|$)/i,
    /\bvat\b/i,
    /\bgst\b/i,
    /\bhst\b/i,
  ]
  for (const rx of tax) {
    if (rx.test(textLower)) return 'tax'
  }
  return null
}

/** Mirrors backend ``classify_im_line`` flag precedence with gratuity-aware tip handling. */
export function classifyImLineFlags(li: {
  description?: string | null
  is_tax?: boolean
  is_shipping?: boolean
  is_discount?: boolean
  is_personal_shopper?: boolean
  is_tip?: boolean
  is_fee?: boolean
}): SharedCostKind | null {
  if (isExcludedSharedCostDescription(li.description)) return null
  if (li.description?.trim().toLowerCase().startsWith('allocated ')) return null
  if (li.is_tax) return 'tax'
  if (li.is_shipping) return 'shipping'
  if (li.is_discount) return 'discount'
  if (li.is_personal_shopper) return 'personal_shopper'
  if (isTipFlagTrustworthy(li.is_tip, li.description)) return 'tip'
  if (li.is_fee) return 'fee'
  return null
}

/** Mirrors ``classify_qbo_line_description`` (keyword gate, then regex kind). */
export function classifySharedKindFromDescription(
  description: string | null | undefined,
  keywords: readonly string[],
): SharedCostKind | null {
  if (!descriptionMatchesSharedCostKeyword(description, keywords)) return null
  const text = description!.trim().toLowerCase()
  return classifyKindFromRegex(text) ?? 'fee'
}
